import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Download, CreditCard, CheckCircle, Clock, AlertTriangle, Loader2, Building2, XCircle } from 'lucide-react';
import api from '../lib/api';

const PortalPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paymentChecked, setPaymentChecked] = useState(false);

  useEffect(() => {
    loadInvoice();
  }, [token]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success' && !paymentChecked) {
      setPaymentChecked(true);
      checkPayment(sessionId);
    }
  }, [searchParams]);

  const loadInvoice = async () => {
    try {
      const res = await api.get(`/portal/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Lien invalide ou expire');
    } finally { setLoading(false); }
  };

  const checkPayment = async (sessionId) => {
    try {
      const res = await api.get(`/portal/${token}/payment-status`, { params: { session_id: sessionId } });
      if (res.data.status === 'paid') {
        loadInvoice();
      }
    } catch (err) { console.error(err); }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await api.get(`/portal/${token}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data?.invoice?.invoice_number || 'facture'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await api.post(`/portal/${token}/pay`, { origin_url: window.location.origin });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    } finally { setPaying(false); }
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat('fr-CH', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' ' + currency;
  };

  const getStatusInfo = (status) => {
    const map = {
      draft: { label: 'Brouillon', icon: FileText, color: 'text-slate-500 bg-slate-100' },
      sent: { label: 'En attente', icon: Clock, color: 'text-amber-600 bg-amber-50' },
      paid: { label: 'Payee', icon: CheckCircle, color: 'text-green-600 bg-green-50' },
      overdue: { label: 'En retard', icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
      cancelled: { label: 'Annulee', icon: XCircle, color: 'text-slate-500 bg-slate-100' },
    };
    return map[status] || map.draft;
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-red-200">
        <CardContent className="p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Lien invalide</h2>
          <p className="text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { invoice, client, company } = data;
  const statusInfo = getStatusInfo(invoice.status);
  const StatusIcon = statusInfo.icon;
  const canPay = ['sent', 'overdue'].includes(invoice.status);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {company?.logo_base64 ? (
              <img src={company.logo_base64} alt="" className="h-10 w-10 rounded-lg object-contain" />
            ) : (
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <div>
              <p className="font-semibold text-slate-900">{company?.name || 'Entreprise'}</p>
              <p className="text-xs text-slate-500">Portail de facturation</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusInfo.color}`}>
            <StatusIcon className="w-4 h-4" />
            {statusInfo.label}
          </div>
        </div>
      </div>

      {/* Payment success banner */}
      {searchParams.get('payment') === 'success' && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3">
          <div className="max-w-3xl mx-auto flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Paiement effectue avec succes ! Merci.</span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Invoice header */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">Facture {invoice.invoice_number}</h1>
                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                  {invoice.invoice_date && <span>Date: {invoice.invoice_date}</span>}
                  {invoice.due_date && <span>Echeance: {invoice.due_date}</span>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-slate-900">{formatCurrency(invoice.total, invoice.currency)}</p>
                {invoice.vat_total > 0 && <p className="text-sm text-slate-500">dont TVA: {formatCurrency(invoice.vat_total, invoice.currency)}</p>}
              </div>
            </div>

            {/* Company & Client */}
            <div className="grid sm:grid-cols-2 gap-6 mb-6 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-400 uppercase font-semibold mb-2">De</p>
                <p className="font-medium text-slate-900">{company?.name}</p>
                {company?.address && <p className="text-sm text-slate-600">{company.address}</p>}
                {company?.postal_code && <p className="text-sm text-slate-600">{company.postal_code} {company.city}</p>}
                {company?.email && <p className="text-sm text-slate-500">{company.email}</p>}
              </div>
              {client && (
                <div>
                  <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Facturer a</p>
                  <p className="font-medium text-slate-900">{client.name}</p>
                  {client.address && <p className="text-sm text-slate-600">{client.address}</p>}
                  {client.postal_code && <p className="text-sm text-slate-600">{client.postal_code} {client.city}</p>}
                </div>
              )}
            </div>

            {/* Items table */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <th className="text-left p-3">Description</th>
                    <th className="text-center p-3">Qte</th>
                    <th className="text-right p-3">Prix unit.</th>
                    <th className="text-right p-3">TVA</th>
                    <th className="text-right p-3">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-3 text-sm text-slate-900">{item.description}</td>
                      <td className="p-3 text-sm text-slate-600 text-center">{item.quantity}</td>
                      <td className="p-3 text-sm text-slate-600 text-right">{formatCurrency(item.unit_price, invoice.currency)}</td>
                      <td className="p-3 text-sm text-slate-600 text-right">{item.vat_rate}%</td>
                      <td className="p-3 text-sm font-medium text-slate-900 text-right">{formatCurrency(item.quantity * item.unit_price, invoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="mt-4 flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Sous-total</span>
                  <span className="text-slate-700">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                </div>
                {invoice.discount_percent > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Remise ({invoice.discount_percent}%)</span>
                    <span className="text-red-500">-{formatCurrency(invoice.subtotal * invoice.discount_percent / 100, invoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">TVA</span>
                  <span className="text-slate-700">{formatCurrency(invoice.vat_total, invoice.currency)}</span>
                </div>
                <div className="flex justify-between text-base font-bold border-t border-slate-200 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(invoice.total, invoice.currency)}</span>
                </div>
              </div>
            </div>

            {invoice.notes && (
              <div className="mt-6 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">Notes :</p>
                <p>{invoice.notes}</p>
              </div>
            )}

            {company?.iban && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">Coordonnees bancaires :</p>
                <p>IBAN: {company.iban}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={handleDownloadPdf}
            data-testid="portal-download-pdf"
          >
            <Download className="w-4 h-4 mr-2" />
            Telecharger le PDF
          </Button>
          {canPay && (
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={handlePay}
              disabled={paying}
              data-testid="portal-pay-btn"
            >
              {paying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              Payer {formatCurrency(invoice.total, invoice.currency)}
            </Button>
          )}
          {invoice.status === 'paid' && (
            <div className="flex-1 flex items-center justify-center gap-2 bg-green-50 text-green-700 font-medium py-3 px-4 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5" />
              Facture payee
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-8">
          Facture generee par {company?.name || 'Xyron Core ERP'} — Portail securise
        </p>
      </div>
    </div>
  );
};

export default PortalPage;
